/* Профиль → «Партнёрская программа» (#partner) — как в прототипе (profile.html, partnerTab).
   Тренер приглашает тренеров по своей ссылке trenergram.ru/p/КОД и получает процент с каждой их оплаты подписки.
   Начисленное переводит на баланс аккаунта (идёт в счёт оплаты Тренерграма) или, если администратор включил
   вывод средств, оставляет заявку на вывод. Счёт и правила — на сервере (services/partner.ts). */
import { trpc } from '@tg/app-core';
import { useState } from 'react';
import { dateOf, dlong, phoneView, rub } from '../lib/format';
import { Modal } from '../ui/Modal';
import { toast } from '../ui/Toast';

const ST: Record<string, [string, string]> = { pending: ['warn', 'ждёт выплаты'], paid: ['ok', 'выплачено'], rejected: ['rm', 'отклонено'] };

/** «3 октября» в текущем году, иначе «3 октября 2025» */
function when(at: Date | string) {
  const iso = dateOf(at)!;
  const d = dlong(iso);
  return iso.slice(0, 4) === String(new Date().getFullYear()) ? d.replace(/ \d{4}$/, '') : d;
}

export function PartnerTab() {
  const q = trpc.trainer.partner.useQuery();
  const [modal, setModal] = useState<null | 'bal' | 'out'>(null);
  const d = q.data;
  if (!d) return null;
  const host = location.host.replace(/^www\./, '');
  const link = `${location.protocol}//${host}/p/${d.code}`;
  const cell = (k: string, v: React.ReactNode, cls = '') => (
    <div className={'bst' + (cls ? ' ' + cls : '')}>
      <s>{k}</s>
      <b>{v}</b>
    </div>
  );
  return (
    <>
      <div className="card bcard">
        <div className="bc-b">
          <div className="bc-h">
            <b>Партнёрская программа</b>
          </div>
          <div className="bc-s">
            Приглашайте тренеров по своей ссылке — с каждой их оплаты подписки вы получаете {d.percent}%, а за первую оплату ещё и бесплатный месяц подписки себе. Начисленное переводите на баланс аккаунта: оно пойдёт в счёт оплаты Тренерграма. Приглашённому — пробный период 30 дней вместо обычных 14.
          </div>
          <div className="invbox prt-link">
            <code>
              {host}/p/<b>{d.code}</b>
            </code>
            <button
              className="btn"
              id="prtCopy"
              onClick={() => {
                try {
                  navigator.clipboard.writeText(link);
                } catch {
                  /* буфер недоступен */
                }
                toast('Ссылка скопирована — отправьте её тренеру');
              }}
            >
              Скопировать ссылку
            </button>
          </div>
          <div className="bstats">
            {cell('Ваш процент', d.percent + '%')}
            {cell(
              'Приглашено',
              <>
                {d.invited}
                <i> · платят {d.paying}</i>
              </>,
            )}
            {cell('Заработано всего', rub(d.earned))}
            {cell('Доступно', rub(d.available), 'pl')}
          </div>
        </div>
        <div className="bc-f prt-f">
          <span>{d.balance ? `На балансе аккаунта ${rub(d.balance)} — пойдёт в оплату подписки` : 'Баланс аккаунта пуст'}</span>
          {d.payoutEnabled && (
            <button className="btn gh sm" id="prtOut" disabled={!d.available} onClick={() => setModal('out')}>
              Вывести
            </button>
          )}
          <button className="btn sm" id="prtBal" disabled={!d.available} onClick={() => setModal('bal')}>
            Перевести на баланс
          </button>
        </div>
      </div>

      <div className="card bcard">
        <div className="bc-b">
          <div className="bc-h">
            <b>Движение по счёту</b>
          </div>
          {d.ledger.length ? (
            <div className="tblwrap bc-tbl">
              <table className="tbl prt-tbl">
                <thead>
                  <tr>
                    <th>Дата</th>
                    <th>Операция</th>
                    <th className="r">Сумма</th>
                  </tr>
                </thead>
                <tbody>
                  {d.ledger.map((o) => (
                    <tr key={o.id} className={o.status === 'rejected' ? 'off' : ''}>
                      <td className="nw">{when(o.createdAt)}</td>
                      <td>
                        {o.type === 'accrual' ? (
                          <>
                            Оплата подписки · {[o.first, o.last].filter(Boolean).join(' ') || 'тренер'}
                            <div className="sub">
                              {rub(o.baseAmount || 0)} × {o.percent}%
                            </div>
                          </>
                        ) : o.type === 'refund' ? (
                          <>Возврат оплаты · {[o.first, o.last].filter(Boolean).join(' ') || 'тренер'}</>
                        ) : o.type === 'bonus_month' ? (
                          <>Бонус за первую оплату · {[o.first, o.last].filter(Boolean).join(' ') || 'тренер'}</>
                        ) : o.type === 'to_balance' ? (
                          'Перевод на баланс аккаунта'
                        ) : (
                          <>
                            Вывод средств <span className={'chip ' + ST[o.status || 'pending'][0]}>{ST[o.status || 'pending'][1]}</span>
                          </>
                        )}
                      </td>
                      <td className={'r nw' + (o.type === 'accrual' || o.type === 'bonus_month' ? ' plus' : '')}>
                        {o.type === 'bonus_month' ? `+${o.days} дн.` : <>{o.type === 'accrual' ? '+' : '−'}{rub(o.amount)}</>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="bc-s">Начислений пока не было — они появятся после первой оплаты приглашённого тренера</div>
          )}
        </div>
      </div>

      {modal && <SpendModal out={modal === 'out'} available={d.available} phone={d.phone} onClose={() => setModal(null)} />}
    </>
  );
}

/** Перевод на баланс и заявка на вывод — одно окно с суммой: по умолчанию всё доступное */
function SpendModal({ out, available, phone, onClose }: { out: boolean; available: number; phone: string | null; onClose: () => void }) {
  const utils = trpc.useUtils();
  const [v, setV] = useState(String(available));
  const done = () => {
    utils.trainer.partner.invalidate();
    utils.trainer.me.invalidate();
    onClose();
  };
  const toBal = trpc.trainer.partnerToBalance.useMutation({ onSuccess: (_r, x) => (done(), toast(rub(x.amount) + ' на балансе аккаунта')), onError: (e) => toast(e.message) });
  const payout = trpc.trainer.partnerPayout.useMutation({ onSuccess: () => (done(), toast('Заявка отправлена — администратор свяжется с вами')), onError: (e) => toast(e.message) });
  const n = Math.floor(+v || 0);
  const ok = n >= 1 && n <= available;
  const busy = toBal.isPending || payout.isPending;
  const go = () => ok && !busy && (out ? payout.mutate({ amount: n }) : toBal.mutate({ amount: n }));
  return (
    <Modal
      title={out ? 'Вывести средства' : 'Перевести на баланс'}
      onClose={onClose}
      foot={
        <>
          <span className="sp"></span>
          <button className="btn gh" onClick={onClose}>
            Отмена
          </button>
          <button className="btn" id="mdPrt" disabled={!ok || busy} onClick={go}>
            {(out ? 'Отправить заявку на ' : 'Перевести ') + (ok ? rub(n) : '')}
          </button>
        </>
      }
    >
      <p className="lead">
        {out
          ? 'Оставьте заявку — администратор Тренерграма свяжется с вами и договорится о выплате. Пока заявка ждёт, сумма в резерве.'
          : 'Деньги перейдут на баланс аккаунта и пойдут в счёт следующего платежа за подписку. Вывести их с баланса будет нельзя.'}
      </p>
      <div className="formfld prt-sum">
        <span className="k">Сумма, ₽</span>
        <input
          className="inp"
          id="prtSum"
          type="number"
          min={1}
          max={available}
          step={1}
          inputMode="numeric"
          value={v}
          autoFocus
          onFocus={(e) => e.target.select()}
          onChange={(e) => setV(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && go()}
        />
      </div>
      <div className="hint" id="prtNote">
        {n > available ? `Доступно только ${rub(available)}` : out ? `Свяжемся с вами по номеру ${phone ? phoneView(phone) : ''}` : `Доступно ${rub(available)}`}
      </div>
    </Modal>
  );
}
